import React, { useCallback, useEffect, useState } from 'react';
import { IOptions } from '@/data/models/option';
import './WriterComposer.scss';
import { IWriterComposer } from '@/data/models/track';
import WriterComposer from './WriterComposer';
import { t } from 'i18next';
import { IMixedField } from '@/data/models/trackMixed';
import _, { isEmpty, isEqual, isNil } from 'lodash';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';

interface WriterComposerProp {
  writerComposer: IWriterComposer[] | IMixedField[] | null | undefined;
  originalWriterComposer?: IMixedField[] | null | undefined;
  handleWriterComposer: any;
  errorBanner?: boolean;
  validationState?: string;
  readonly?: boolean;
  disabled?: boolean;
  multiTrack?: boolean;
  showChangeIndicator?: boolean;
}

export default function WriterComposerContainer({
  writerComposer,
  originalWriterComposer,
  handleWriterComposer,
  errorBanner,
  validationState,
  readonly,
  disabled,
  multiTrack = false,
  showChangeIndicator = false,
}: WriterComposerProp) {
  const [inputs, setInputs] = useState<any>(writerComposer);

  const handleAddInput = () => {
    let newWriterComposer = [];
    if (multiTrack) {
      newWriterComposer = [
        ...inputs.map((a: any) => ({ ...a })),
        {
          value: { role: 'Writer', name: '' },
          originalValue: null,
          isMixed: false,
          isReadOnly: false,
        },
      ];
    } else {
      newWriterComposer = [
        ...inputs.map((a: any) => ({ ...a })),
        { role: 'Writer', name: '' },
      ];
    }

    setInputs(newWriterComposer);
    handleWriterComposer(newWriterComposer);
  };

  useEffect(() => {
    if (!isNil(writerComposer) && !isEmpty(writerComposer)) {
      setInputs(writerComposer);
    } else {
      if (multiTrack) {
        setInputs([
          {
            value: { role: 'Writer', name: '' },
            originalValue: null,
            isMixed: false,
            isReadOnly: false,
          },
        ]);
      } else {
        setInputs([{ role: 'Writer', name: '' }]);
      }
    }
  }, [writerComposer]);

  const handleRoleChange = (selectedOption: any, index: any) => {
    const onChangeValue = _.cloneDeep(inputs);

    if (multiTrack) {
      if (!onChangeValue[index].value) {
        onChangeValue[index].value = { role: selectedOption.value, name: null };
      } else {
        onChangeValue[index].value.role = selectedOption.value;
      }
    } else {
      onChangeValue[index].role = selectedOption ? selectedOption.value : '';
    }

    setInputs(onChangeValue);
    handleWriterComposer(onChangeValue);
  };

  const handleChange = (event: any, index: any) => {
    const { value } = event.target;
    const onChangeValue = _.cloneDeep(inputs);

    if (multiTrack) {
      if (!onChangeValue[index].value) {
        onChangeValue[index].value = { role: null, name: value };
      }
      onChangeValue[index].value.name = value;
    } else {
      onChangeValue[index].name = value;
    }

    setInputs(onChangeValue);
    handleWriterComposer(onChangeValue);
  };

  const handleDeleteInput = (index: any) => {
    const newArray = _.cloneDeep(inputs);

    if (newArray.length === 1) {
      if (multiTrack) {
        if (isEmpty(originalWriterComposer)) {
          return undoChanges();
        } else {
          newArray[0] = {
            value: { role: 'Writer', name: '' },
            originalValue: { role: 'Writer', name: '' },
            isMixed: false,
            isReadOnly: false,
          };
        }
      } else {
        newArray[0] = { role: 'Writer', name: '' };
      }
    } else {
      newArray.splice(index, 1);
    }

    setInputs(newArray);
    handleWriterComposer(newArray);
  };

  const undoChanges = useCallback(() => {
    handleWriterComposer(_.cloneDeep(originalWriterComposer));
  }, [originalWriterComposer]);

  const writerComposerRole: IOptions[] = [
    {
      value: 'Writer',
      label: t('track.writer'),
    },
    {
      value: 'Composer',
      label: t('track.composer'),
    },
    {
      value: 'Both',
      label: t('track.writerComposer'),
    },
  ];
  return (
    <>
      {multiTrack && showChangeIndicator ? (
        <MultiTrackChangeIndicator
          show={!isEqual(writerComposer, originalWriterComposer)}
          onClick={() => undoChanges()}
        ></MultiTrackChangeIndicator>
      ) : null}
      <div className="list-builder">
        {inputs?.map((item: any, index: any) => {
          let writerComposerItem = item;
          let isReadOnly = readonly;
          let customInputClass = null;
          let customDropdownClass = null;
          let isMixedNamePlaceholder = false;
          let isMixedRolePlaceholder = false;

          if (multiTrack) {
            if (item?.isReadOnly) {
              isReadOnly = item?.isReadOnly;
            }
            writerComposerItem = item.value;
            isMixedNamePlaceholder =
              item.isMixed &&
              writerComposerItem?.name === 'Mixed' &&
              item?.originalValue.name === 'Mixed';
            isMixedRolePlaceholder =
              item.isMixed && writerComposerItem?.role === 'Mixed';
            if (isMixedNamePlaceholder) {
              customInputClass = 'mixed-input-text';
            }
            if (isMixedRolePlaceholder) {
              customDropdownClass = 'mixed-dropdown-text';
            }
          }

          if (!writerComposerItem) return;
          return (
            <WriterComposer
              key={index}
              index={index}
              errorBanner={errorBanner}
              validationState={validationState}
              item={writerComposerItem}
              handleChange={handleChange}
              inputs={inputs}
              inputCustomClass={customInputClass}
              dropdownCustomClass={customDropdownClass}
              handleDeleteInput={handleDeleteInput}
              handleAddInput={handleAddInput}
              handleRoleChange={handleRoleChange}
              WriterComposerRole={writerComposerRole}
              readonly={isReadOnly}
              disabled={disabled}
              mixedPlaceholder={isMixedNamePlaceholder}
            ></WriterComposer>
          );
        })}
      </div>
    </>
  );
}
